import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { FC, useState } from 'react';

import { PieceAction } from '@apiContract';
import Footer from '@components/DrawerContent/Footer';
import Title from '@components/DrawerContent/Title';

import {
  sxActionButton,
  sxActionIcon,
  sxActionItem,
  sxActionsList,
  sxDrawerPaper,
  sxP20,
} from '../ForceActionsDrawer/style';
import { useValidationExitGuard } from '../hooks/useValidationExitGuard';
import { ActionComponentProps, mailpieceActions } from '../index';

type Props = ActionComponentProps & {
  title: string;
  actions: PieceAction[];
};

export const CombinedActionsDrawer: FC<Props> = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  pendingSearchValue,
  validationData,
  title,
  actions,
}) => {
  const [selectedAction, setSelectedAction] = useState<PieceAction | null>(null);
  const [startedAction, setStartedAction] = useState<PieceAction | null>(null);

  const StartedActionComponent = startedAction ? mailpieceActions[startedAction]?.Component : null;

  const handleClose = () => {
    setSelectedAction(null);
    setStartedAction(null);
    onActionFinish();
  };

  const { guardedExit, exitConfirmDialog } = useValidationExitGuard(validationData, handleClose);

  return (
    <>
      <Drawer open={actionStarted} anchor="right" onClose={guardedExit} slotProps={{ paper: sxDrawerPaper }}>
        <Title title={title} onClose={guardedExit} />

        <Box sx={sxActionsList}>
          <List disablePadding>
            {actions.map((action) => {
              const actionConfig = mailpieceActions[action];
              if (!actionConfig) return null;

              const { title: actionTitle, Icon, actionTriggerColor } = actionConfig;
              const isSelected = selectedAction === action;

              return (
                <Box key={action} sx={{ mb: 1, mx: 1 }}>
                  <ListItem disablePadding sx={sxActionItem(isSelected)}>
                    <ListItemButton
                      onClick={() => setSelectedAction(isSelected ? null : action)}
                      disabled={startedAction !== null}
                      sx={sxActionButton(actionTriggerColor)}
                    >
                      <ListItemIcon sx={sxActionIcon}>
                        <Icon color="inherit" />
                      </ListItemIcon>
                      <ListItemText
                        primary={typeof actionTitle === 'function' ? actionTitle(mailpieces[0]) : actionTitle}
                      />
                    </ListItemButton>
                  </ListItem>
                </Box>
              );
            })}
          </List>
        </Box>

        <Footer
          sx={sxP20}
          disabled={!selectedAction || startedAction !== null}
          title="Continue"
          handleSubmit={() => setStartedAction(selectedAction)}
        />
      </Drawer>

      {exitConfirmDialog}

      {StartedActionComponent && (
        <StartedActionComponent
          mailpieces={mailpieces}
          actionStarted
          onActionFinish={handleClose}
          pendingSearchValue={pendingSearchValue}
          validationData={validationData}
        />
      )}
    </>
  );
};
